import type { Module } from 'vuex';
import { User } from '@/types/User';
import {RootState} from "@/store";

export interface UsersState {
  users: User[];
}

const usersModule: Module<UsersState, RootState> = {
  namespaced: true,

  state: (): UsersState => ({
    users: [],
  }),

  getters: {
    getById: (state) => (idUser: number): User | undefined => {
      return state.users.find(user => user.idUser === idUser);
    },
  },

  mutations: {
    setUsers(
      state,
      payload: { users: Record<number, any> }
    ) {
      state.users = Object
        .values(payload.users)
        .map(raw => User.fromRaw(raw));
    },

    setUserCreate(
      state,
      payload: {
        idUser: number;
        email: string;
        name: string;
        idLanguage?: number | null;
        admin?: boolean;
        block?: boolean;
        confirmEmail?: boolean;
        theme?: string | null;
      }
    ) {
      const raw = {
        idUser: payload.idUser,
        email: payload.email,
        name: payload.name,
        idLanguage: payload.idLanguage ?? null,
        admin: payload.admin ?? false,
        block: payload.block ?? false,
        confirmEmail: payload.confirmEmail ?? false,
        theme: payload.theme ?? null,
      };
      state.users.push(User.fromRaw(raw));
    },

    setUserUpdate(
      state,
      payload: {
        idUser: number;
        name?: string;
        admin?: boolean;
        block?: boolean;
        theme?: string | null;
      }
    ) {
      const user = state.users.find(user => user.idUser === payload.idUser);
      if (!user) return;
      if (payload.name !== undefined) user.name = payload.name;
      if (payload.admin !== undefined) user.admin = payload.admin;
      if (payload.block !== undefined) user.block = payload.block;
      if (payload.theme !== undefined) user.theme = payload.theme;
    },

    setUserDelete(
      state,
      payload: { idUser: number }
    ) {
      state.users = state.users.filter(
        user => user.idUser !== payload.idUser
      );
    },

    setUserEmailConfirm(
      state,
      payload: { idUser: number }
    ) {
      const user = state.users.find(user => user.idUser === payload.idUser);
      if (user) user.confirmEmail = true;
    },

    setUserName(
      state,
      payload: {
        idUser: number;
        name: string;
      }
    ) {
      const user = state.users.find(user => user.idUser === payload.idUser);
      if (user) user.name = payload.name;
    },

    setUserTheme(
      state,
      payload: {
        idUser: number;
        theme: string | null;
      }
    ) {
      const user = state.users.find(user => user.idUser === payload.idUser);
      if (user) user.theme = payload.theme;
    },

    setUserChangeEmail(
      state,
      payload: {
        idUser: number;
        email: string;
      }
    ) {
      const user = state.users.find(user => user.idUser === payload.idUser);
      if (user) user.email = payload.email;
    },
  },

  actions: {
    wsSetUsers({ commit }, payload) {
      commit('setUsers', payload);
    },
    wsUserCreate({ commit }, payload) {
      commit('setUserCreate', payload);
    },
    wsUserUpdate({ commit }, payload) {
      commit('setUserUpdate', payload);
    },
    wsUserDelete({ commit }, payload) {
      commit('setUserDelete', payload);
    },
    wsUserEmailConfirm({ commit }, payload) {
      commit('setUserEmailConfirm', payload);
    },
    wsUserName({ commit }, payload) {
      commit('setUserName', payload);
    },
    wsUserTheme({ commit }, payload) {
      commit('setUserTheme', payload);
    },
    wsUserChangeEmail({ commit }, payload) {
      commit('setUserChangeEmail', payload);
    },
  },
};

export default usersModule; 